import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { GoogleLogin } from '@react-oauth/google'
import { useAuthStore } from '../store/authStore'
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion'
import { googleClientId, isTestMode } from '../config'

const features = [
    { word: 'drag & drop', delay: 0.1 },
    { word: 'grid based', delay: 0.2 },
    { word: 'free forever', delay: 0.3 },
    { word: 'resizable', delay: 0.4 },
    { word: 'fast', delay: 0.5 },
]

const floatingCards = [
    { src: '/landing/audiocard.webp', top: '10%', left: '6%', width: 168, rotateZ: -8, rotateY: 18, rotateX: -10, speed: 0.15, blur: 0.4, opacity: 0.34, hue: -8, duration: 18.5, yFloat: 18, scale: 1.02 },
    { src: '/landing/link card.webp', top: '66%', left: '8%', width: 158, rotateZ: -4, rotateY: -12, rotateX: 7, speed: 0.08, blur: 1.2, opacity: 0.2, hue: 10, duration: 19.5, yFloat: 12, scale: 0.98 },
    { src: '/landing/profile card.webp', top: '30%', right: '6%', width: 178, rotateZ: 10, rotateY: -16, rotateX: 6, speed: 0.12, blur: 0.2, opacity: 0.3, hue: -12, duration: 19.5, yFloat: 16, scale: 1.03 },
    { src: '/landing/social ikonkart.webp', top: '72%', right: '14%', width: 94, rotateZ: -6, rotateY: -22, rotateX: 2, speed: 0.18, blur: 0.6, opacity: 0.22, hue: 20, duration: 17.2, yFloat: 14, scale: 1 },
    { src: '/landing/soundcart (2).webp', top: '38%', left: '17%', width: 166, rotateZ: -10, rotateY: 12, rotateX: 6, speed: 0.05, blur: 1.4, opacity: 0.18, hue: 18, duration: 22, yFloat: 10, scale: 0.97 },
    { src: '/landing/text card.webp', top: '78%', left: '22%', width: 226, rotateZ: 6, rotateY: 8, rotateX: 0, speed: 0.3, blur: 0.3, opacity: 0.24, hue: -18, duration: 21.5, yFloat: 20, scale: 1.01 },
    { src: '/landing/videoplayer.webp', top: '7%', right: '16%', width: 170, rotateZ: 7, rotateY: -8, rotateX: 18, speed: 0.14, blur: 0.8, opacity: 0.26, hue: 8, duration: 18.8, yFloat: 15, scale: 1.02 },
]

function ParallaxBackground() {
    const { scrollY } = useScroll()

    return (
        <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ perspective: '1000px' }}>
            {/* Background Grid */}
            <div 
                className="absolute inset-0 opacity-[0.03]"
                style={{
                    backgroundImage: `
                        linear-gradient(to right, #ffffff 1px, transparent 1px),
                        linear-gradient(to bottom, #ffffff 1px, transparent 1px)
                    `,
                    backgroundSize: '32px 32px',
                    maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
                    WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
                }}
            />

            {/* Scattered Cards - Desktop only */}
            <div className="hidden md:block">
                {floatingCards.map((card, i) => {
                    const yOffset = useTransform(scrollY, [0, 1000], [0, -card.speed * 1000])
                    
                    return (
                        <motion.div
                            key={i}
                            className="absolute will-change-transform"
                            style={{
                                top: card.top,
                                left: card.left,
                                right: card.right,
                                width: card.width,
                                y: yOffset,
                            }}
                        >
                            <motion.div
                                className="relative will-change-transform"
                                animate={{
                                    y: [0, -card.yFloat, 0],
                                    rotateZ: [card.rotateZ, card.rotateZ + 1.8, card.rotateZ],
                                    scale: [card.scale, card.scale + 0.018, card.scale],
                                }}
                                transition={{
                                    duration: card.duration,
                                    repeat: Infinity,
                                    ease: 'easeInOut',
                                    delay: i * 0.35,
                                }}
                                style={{
                                    rotateY: card.rotateY,
                                    rotateX: card.rotateX,
                                    transformStyle: 'preserve-3d',
                                    opacity: card.opacity,
                                    filter: `blur(${card.blur}px) saturate(0.9) hue-rotate(${card.hue}deg)`,
                                }}
                            >
                                <div
                                    className="absolute inset-[-10%] rounded-[28px]"
                                    style={{
                                        background: 'radial-gradient(circle at 50% 50%, rgba(146, 196, 255, 0.16), rgba(255,255,255,0.02) 48%, transparent 72%)',
                                        filter: 'blur(18px)',
                                        opacity: 0.8,
                                    }}
                                />
                                <div className="absolute inset-0 rounded-[24px] bg-gradient-to-br from-white/12 via-white/[0.03] to-transparent" />
                                <img
                                    src={card.src}
                                    alt=""
                                    className="relative w-full h-auto rounded-[24px] shadow-[0_28px_80px_rgba(0,0,0,0.28)] mix-blend-screen"
                                    style={{
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        boxShadow: '0 24px 60px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)',
                                    }}
                                />
                            </motion.div>
                        </motion.div>
                    )
                })}
            </div>
        </div>
    )
}

export default function LandingPage() {
    const navigate = useNavigate()
    const { user, isAuthenticated, isLoading, login, loginTest } = useAuthStore()
    const [privacyOpen, setPrivacyOpen] = useState(false)

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-6 h-6 border border-white/20 border-t-white/80"
                />
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-[#050505] text-white overflow-hidden relative">
            <ParallaxBackground />

            {/* Subtle gradient orb */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-white/[0.02] via-transparent to-transparent rounded-full blur-3xl" />
            </div>

            <div className="relative min-h-screen flex flex-col px-6 z-10">
                {/* Main Content */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2 }}
                    className="flex-1 flex flex-col items-center justify-center text-center max-w-4xl mx-auto w-full py-14"
                >
                    {/* Logo / Brand */}
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-6xl md:text-8xl font-light tracking-tight mb-6"
                        style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
                    >
                        oda<span className="text-white/40">.bio</span>
                    </motion.h1>

                    {/* Tagline */}
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        className="text-lg md:text-xl text-white/50 font-light mb-12"
                    >
                        the most satisfying way to build a bio
                    </motion.p>

                    {/* Video Showcase */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.7 }}
                        className="relative mb-14"
                    >
                        <div className="relative overflow-hidden bg-white/[0.03] border border-white/[0.06] backdrop-blur-sm">
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full max-w-2xl mx-auto"
                                style={{ display: 'block' }}
                            >
                                <source src="/video/flexiblecards.webm" type="video/webm" />
                            </video>
                            {/* Gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent pointer-events-none opacity-60" />
                        </div>
                    </motion.div>

                    {/* Feature words */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mb-14"
                    >
                        {features.map((feature, index) => (
                            <motion.span
                                key={feature.word}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 1 + feature.delay }}
                                className="text-sm md:text-base text-white/40 font-light tracking-wide"
                            >
                                {feature.word}
                                {index < features.length - 1 && (
                                    <span className="ml-3 text-white/15">·</span>
                                )}
                            </motion.span>
                        ))}
                    </motion.div>

                    {/* Auth Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.5 }}
                    >
                        {isAuthenticated && user ? (
                            <div className="space-y-4">
                                <p className="text-white/50 text-sm mb-6">
                                    Welcome back, <span className="text-white/80">{user.name}</span>
                                </p>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => navigate('/edit')}
                                    className="group relative px-10 py-4 bg-white text-[#050505] font-medium text-sm tracking-wide overflow-hidden transition-all duration-300 hover:bg-white/90"
                                >
                                    <span className="relative z-10">Edit Your Page</span>
                                </motion.button>
                                {user.subdomain && (
                                    <button
                                        onClick={() => navigate(`/${user.subdomain}`)}
                                        className="block mx-auto mt-4 text-sm text-white/40 hover:text-white/60 transition-colors"
                                    >
                                        view {user.subdomain}.oda.bio →
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {isTestMode && (
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={async () => {
                                            try {
                                                await loginTest()
                                                navigate('/edit')
                                            } catch (error) {
                                                console.error('Test login failed:', error)
                                            }
                                        }}
                                        className="px-10 py-4 bg-white/10 text-white font-medium text-sm tracking-wide border border-white/10 hover:bg-white/15 transition-all duration-300"
                                    >
                                        Test Mode Login
                                    </motion.button>
                                )}

                                {!isTestMode && googleClientId && (
                                    <div className="google-login-wrapper w-full flex justify-center">
                                        <style>{`
                      .google-login-wrapper > div {
                        display: flex;
                        justify-content: center;
                      }
                      .google-login-wrapper [role="button"] {
                        border-radius: 0 !important;
                        font-family: 'Outfit', system-ui, sans-serif !important;
                        transition: all 0.3s ease !important;
                        box-shadow: none !important;
                        border: 1px solid rgba(255, 255, 255, 0.1) !important;
                      }
                      .google-login-wrapper [role="button"]:hover {
                        box-shadow: 0 0 30px rgba(255, 255, 255, 0.1) !important;
                      }
                    `}</style>
                                        <GoogleLogin
                                            onSuccess={async (credentialResponse) => {
                                                try {
                                                    if (!credentialResponse.credential) throw new Error('No credential returned')
                                                    await login(credentialResponse.credential)
                                                    navigate('/edit')
                                                } catch (error) {
                                                    console.error('Login failed:', error)
                                                }
                                            }}
                                            onError={() => console.error('Login Failed')}
                                            theme="filled_black"
                                            shape="rectangular"
                                            size="large"
                                            text="continue_with"
                                            width={280}
                                        />
                                    </div>
                                )}

                                {!isTestMode && !googleClientId && (
                                    <p className="text-sm text-white/40">
                                        Google Client ID not found
                                    </p>
                                )}
                            </div>
                        )}
                    </motion.div>
                </motion.div>

                {/* Footer */}
                <motion.footer
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.8 }}
                    className="w-full pb-6 pt-3 text-center"
                >
                    <div className="text-[11px] text-white/25 font-light">
                        Built at Yıldız Technical University for creators · © 2026 oda.bio ·{' '}
                        <button
                            type="button"
                            onClick={() => setPrivacyOpen(true)}
                            className="text-white/35 hover:text-white/55 transition-colors underline underline-offset-2"
                        >
                            Privacy
                        </button>
                    </div>
                </motion.footer>
            </div>

            <AnimatePresence>
                {privacyOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
                        onClick={() => setPrivacyOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.96, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.96, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="w-full max-w-lg bg-[#0a0a0a] border border-white/10 p-6"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-medium tracking-wide text-white/90" style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}>
                                    Privacy
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setPrivacyOpen(false)}
                                    className="text-white/40 hover:text-white/70 transition-colors"
                                    aria-label="Close privacy"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="text-sm text-white/60 leading-relaxed space-y-3">
                                <p>We only collect the information needed to create and run your page.</p>
                                <p>This includes basic account details provided by Google.</p>
                                <p>Your data is never sold or shared with third parties for advertising.</p>
                                <p>We don’t track you across the internet.</p>
                                <p>Anything you add to your page is under your control, and you can delete it anytime.</p>
                                <p>We use simple analytics to understand how the site is used and to improve the experience.</p>
                                <p>If you ever want your data removed, just contact us and we’ll take care of it.</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

